import I18n from 'I18n';
import { fromJS, List, Map as ImmutableMap } from 'immutable';
//@ts-expect-error Untyped import
import { getLastYearFilter, getPriorPeriodFilter } from '../../compare';
import * as DataTypes from '../../constants/dataTypes';
import { RANGE_TYPES } from '../../constants/dateRangeTypes';
import { DEFAULT_NULL_VALUES } from '../../constants/defaultNullValues';
import { SUM } from '../../constants/metricTypes';
import { NUMERIC_PROPERTIES } from '../../constants/numericProperties';
import { BETWEEN, EQ, HAS_PROPERTY, IN, LTE, NEQ, NOT_HAS_PROPERTY } from '../../constants/operators';
import { ACCUMULATE } from '../../constants/processorTypes';
import { DATE, DATE_TIME, ENUMERATION, STRING } from '../../constants/property-types';
//@ts-expect-error Untyped import
import { endOf } from '../../lib/dateUtils';
//@ts-expect-error Untyped import
import makeDateRangeByType from '../../lib/makeDateRangeByType';
import { getProperty } from '../../properties/getProperty';
import { extractPropertyNamespace } from '../../properties/namespaceProperty';
//@ts-expect-error Untyped import
import { get as isUnified } from '../../retrieve/unified/supported';
//@ts-expect-error Untyped import
import { Filter } from '../filters';
import { DEFAULT_FISCAL_YEAR_MONTH_NAME } from '../../constants/dates';
import { YEAR } from '../../constants/frequency';
import { getFiscalPeriodOffset, getUseFiscalYear, getUseFiscalYearInAggregation, isFiscalYearSupportedPeriod, isFiscalYearSupportedRangeType } from '../../lib/fiscalYearUtils';
import { INBOUND_DB_DATA_TYPES } from '../../constants/dataTypes';
import { getDataTypeFromConfig } from '../../report/reportGetters';
//@ts-expect-error Untyped import
import { isExplicitlyBlocklistedForFiltering } from '../../public/blocklisted-for-filtering';
import { getCustomFilters, getDimensionFromConfig, getFilterGroups, getFilters } from '../../report/configReportGetters';
import { applyFiltersToFilterGroups } from './../../report/configReportSetters';
const STRICT_EQUALITY_ONLY_TYPES = [DataTypes.EMAIL];
const getMetricFilter = (config, keys) => {
  const metricKey = keys.get(keys.count() - 2);
  const metricType = keys.last();
  const dataType = getDataTypeFromConfig(config);
  return getProperty(dataType, metricKey).then(prop => {
    let filters = List();
    if (NUMERIC_PROPERTIES.includes(prop.get('type')) && metricKey !== 'count' && !isExplicitlyBlocklistedForFiltering(prop) && !prop.get('scripted')) {
      if (metricType === SUM) {
        filters = filters.push(fromJS({
          property: prop.get('name'),
          operator: NEQ,
          value: 0
        }));
      }
      filters = filters.push(fromJS({
        property: prop.get('name'),
        operator: HAS_PROPERTY
      }));
    }
    return filters;
  });
};
const addMetricFilter = (config, keys, filters) => {
  return getMetricFilter(config, keys).then(metricFilter => metricFilter ? filters.updateIn(['custom'], customFilters => customFilters.concat(metricFilter)) : filters);
};
export const fixCompareFilters = (config, keys, options = {}) => filters => {
  const compare = config.get('compare');
  if (!compare) {
    return filters;
  }
  const useFiscalYear = getUseFiscalYear(config);
  return filters.updateIn(['dateRange', 'value'], currentDateRange => {
    const fullDateRange = fromJS(makeDateRangeByType(currentDateRange.toJS(), 'YYYY-MM-DD', config.get('dataType'), useFiscalYear, options.fiscalYearMonthName));
    const compareDateRange = compare === 'PRIOR_YEAR' ? getLastYearFilter(fullDateRange) : getPriorPeriodFilter(fullDateRange, config.get('dataType'), useFiscalYear);
    return options.shouldUseCompare ? compareDateRange : currentDateRange;
  });
};
const createDealProgressFilter = value => {
  const property = 'dealstage.probability';
  return value === 'OPEN' ? Filter({
    property,
    operator: 'NOT_IN',
    values: List([0, 1])
  }) : Filter({
    property,
    operator: 'EQ',
    value: value === 'WON' ? 1 : 0
  });
};
const addRawPropertySuffix = (dataType, propertyType) => INBOUND_DB_DATA_TYPES.includes(dataType) && propertyType === STRING ? {
  propertySuffix: 'raw'
} : {};
const unboundedRanges = [RANGE_TYPES.IS_AFTER_DATE, RANGE_TYPES.ALL];
const DATE_FORMAT = 'YYYY-MM-DD';
const isGreaterThanOrEqualToDate = (dateOne, dateTwo) => dateOne.isAfter(dateTwo) || dateOne.isSame(dateTwo);
const isLessThanOrEqualToDate = (dateOne, dateTwo) => dateOne.isBefore(dateTwo) || dateOne.isSame(dateTwo);

// consolidates current date range with drilled-down date
// invariant: new date range should never include dates omitted from original
export const getUpdatedDateRange = (drilldownDate, config, options = {}) => {
  const dataType = config.get('dataType');
  const frequency = config.get('frequency');
  const dateRange = config.getIn(['filters', 'dateRange']);
  const dateValue = dateRange.get('value').toJS();
  const rangeType = dateRange.getIn(['value', 'rangeType'], '');
  const isCompleteRangeType = [RANGE_TYPES.ALL, RANGE_TYPES.ROLLING, RANGE_TYPES.IS_EQUAL_TO, RANGE_TYPES.IS_BEFORE_DATE, RANGE_TYPES.IS_AFTER_DATE, RANGE_TYPES.CUSTOM].includes(rangeType);
  const entireCurrentUnit = dateValue.entireCurrentUnit || isCompleteRangeType;
  const useFiscalYear = dateRange.get('useFiscalYear', false) || isCompleteRangeType;
  const useFiscalYearInAggregation = config.get('useFiscalYearInAggregation', false);
  const fiscalYearMonthName = options.fiscalYearMonthName || DEFAULT_FISCAL_YEAR_MONTH_NAME;
  const isComparisonDrilldown = options.datasetKey === 'compare' && rangeType === 'CUSTOM';
  const isValidFiscalYearRange = isFiscalYearSupportedPeriod(frequency, fiscalYearMonthName) && (isFiscalYearSupportedRangeType(rangeType) || isComparisonDrilldown);
  const shouldAggregateOnFiscalYear = useFiscalYearInAggregation && isValidFiscalYearRange;
  const isSplitFiscalYearRange = (useFiscalYear && !useFiscalYearInAggregation || !useFiscalYear && useFiscalYearInAggregation) && isValidFiscalYearRange;
  const currentRange = makeDateRangeByType(dateValue, DATE_FORMAT, dataType, useFiscalYear, fiscalYearMonthName);
  const currStart = I18n.moment.portalTz(currentRange.startDate, DATE_FORMAT);
  const currEnd = I18n.moment.portalTz(currentRange.endDate, DATE_FORMAT);
  let drilldownStandardPeriodStart = I18n.moment.portalTz(drilldownDate, DATE_FORMAT);
  if (shouldAggregateOnFiscalYear) {
    drilldownStandardPeriodStart = drilldownStandardPeriodStart.subtract(1, 'year');
  }
  const drilldownStandardPeriodEnd = endOf({
    date: drilldownStandardPeriodStart,
    frequency,
    dataType,
    useFiscalYear: false,
    fiscalYearMonthName
  });
  const fiscalYearOffset = getFiscalPeriodOffset(YEAR, fiscalYearMonthName);
  const drilldownFiscalPeriodStart = drilldownStandardPeriodStart.clone().add(fiscalYearOffset, 'months');
  let drilldownFiscalPeriodEnd = drilldownStandardPeriodEnd.clone().add(fiscalYearOffset, 'months').endOf('month');
  if (!entireCurrentUnit || isComparisonDrilldown) {
    drilldownFiscalPeriodEnd = I18n.moment.min(drilldownFiscalPeriodEnd, currEnd);
  }

  /**
   * When a report's useFiscalYear and useFiscalYearInAggregation
   * properties aren't the same and the report has a QUARTER or
   * YEAR frequency, the beginning and end periods of the date
   * range as a whole need to be calculated differently than periods
   * in the middle. This checks for these edge cases.
   */

  const isUseFiscalYearEdgeCase = useFiscalYear && (currStart.isAfter(drilldownStandardPeriodStart) || currEnd.isBefore(drilldownStandardPeriodEnd));
  const isUseFiscalYearInAggregationEdgeCase = shouldAggregateOnFiscalYear && (currStart.isAfter(drilldownFiscalPeriodStart) || currEnd.isBefore(drilldownFiscalPeriodEnd));
  const isFiscalPeriodEdgeCase = isSplitFiscalYearRange && (isUseFiscalYearEdgeCase || isUseFiscalYearInAggregationEdgeCase);
  const conflictingStart = shouldAggregateOnFiscalYear ? drilldownFiscalPeriodStart : drilldownStandardPeriodStart;
  const conflictingEnd = shouldAggregateOnFiscalYear ? drilldownFiscalPeriodEnd : drilldownStandardPeriodEnd;
  const startDateIsContained = isGreaterThanOrEqualToDate(conflictingStart, currStart) && isLessThanOrEqualToDate(conflictingStart, currEnd);
  const endDateIsContained = isGreaterThanOrEqualToDate(conflictingEnd, currStart) && isLessThanOrEqualToDate(conflictingEnd, currEnd);

  /** If we're generating a dimension filter,
   * we want to return the entire date range
   * without consideration for the base dateRange
   * filter, as it has its own independent values
   */
  if (options.isDimensionDrilldown && !(startDateIsContained && endDateIsContained)) {
    return ImmutableMap({
      rangeType: RANGE_TYPES.CUSTOM,
      startDate: conflictingStart.format(DATE_FORMAT),
      endDate: conflictingEnd.format(DATE_FORMAT)
    });
  }
  if (isFiscalPeriodEdgeCase) {
    return ImmutableMap({
      rangeType: RANGE_TYPES.CUSTOM,
      startDate: I18n.moment.max(currStart, conflictingStart).format(DATE_FORMAT),
      endDate: I18n.moment.min(currEnd, conflictingEnd).format(DATE_FORMAT)
    });
  }
  if (shouldAggregateOnFiscalYear) {
    return ImmutableMap({
      rangeType: RANGE_TYPES.CUSTOM,
      startDate: drilldownFiscalPeriodStart.format(DATE_FORMAT),
      endDate: drilldownFiscalPeriodEnd.format(DATE_FORMAT)
    });
  }
  const isAccumulated = config.get('processors', List()).includes(ACCUMULATE);
  const startDate = isAccumulated || currStart.isAfter(drilldownDate) ? currentRange.startDate : drilldownDate;
  const endDate = unboundedRanges.includes(currentRange.rangeType) ? drilldownStandardPeriodEnd.format(DATE_FORMAT) : I18n.moment.min(currEnd, drilldownStandardPeriodEnd).format(DATE_FORMAT);
  return ImmutableMap({
    rangeType: RANGE_TYPES.CUSTOM,
    startDate,
    endDate
  });
};
export const getFilter = (property, propertyInfo = ImmutableMap(), value, config, options) => {
  const dataType = config.get('dataType');
  const type = propertyInfo.get('type') || 'string';
  if (type === DATE_TIME || type === DATE) {
    // substitute WEEK with ISO WEEK to match contacts search's definition of week until contacts search becomes locale aware
    const frequency = config.get('frequency');
    if (getUseFiscalYear(config) || getUseFiscalYearInAggregation(config)) {
      const updatedDateRange = getUpdatedDateRange(value, config, options);
      return fromJS({
        property,
        operator: BETWEEN,
        dateTimeFormat: 'DATE',
        value: updatedDateRange.get('startDate'),
        highValue: updatedDateRange.get('endDate')
      });
    }
    const highValue = endOf({
      date: I18n.moment.portalTz(value, 'YYYY-MM-DD'),
      frequency,
      dataType
    }).format('YYYY-MM-DD');
    if (config.get('processors', List()).includes('accumulate')) {
      return fromJS({
        property,
        operator: LTE,
        dateTimeFormat: 'DATE',
        value: highValue
      });
    }
    return fromJS({
      property,
      operator: BETWEEN,
      dateTimeFormat: 'DATE',
      value,
      highValue
    });
  }

  // TODO: eliminate this special case
  if (property === 'BUCKET_dealProgress' || property === 'dealstage.probability') {
    return createDealProgressFilter(value);
  }

  // RA-14844: Check that data named "undefined" can be used as a drilldown
  if (value === DEFAULT_NULL_VALUES.STRING || String(value) === String(propertyInfo.get('defaultNullValue')) && typeof value !== 'string' && propertyInfo.has('defaultNullValue')) {
    return Filter({
      property,
      operator: NOT_HAS_PROPERTY
    });
  }
  if (type === ENUMERATION && !isUnified(dataType) && !STRICT_EQUALITY_ONLY_TYPES.includes(dataType)) {
    return Filter(Object.assign({
      property,
      operator: IN,
      values: [value]
    }, addRawPropertySuffix(dataType, type)));
  }
  return Filter(Object.assign({
    property,
    operator: EQ,
    value
  }, addRawPropertySuffix(dataType, type)));
};
const getDimensionFilters = (config, keys, options = {}) => {
  const dataType = getDataTypeFromConfig(config);
  const dateRange = config.getIn(['filters', 'dateRange']);
  const dimensions = getDimensionFromConfig(config);
  return Promise.all(dimensions.map(property => {
    if (dataType === DataTypes.CROSS_OBJECT) {
      const {
        dataType: xoDataType,
        propertyName
      } = extractPropertyNamespace(property);
      return getProperty(xoDataType, propertyName);
    }
    return getProperty(dataType, property);
  }).toJS()).then(propInfo => dimensions.reduce((updatedFilters, property, index) => {
    if (keys.count() <= index + 2) {
      return updatedFilters;
    }
    const value = keys.get(index);
    if (dateRange && property === dateRange.get('property')) {
      const newDateValue = getUpdatedDateRange(value, config, options);
      return Object.assign({}, updatedFilters, {
        dateRangeFilter: dateRange.set('value', newDateValue)
      });
    }
    const dimensionOptions = Object.assign({}, options, {
      isDimensionDrilldown: true
    });
    return Object.assign({}, updatedFilters, {
      customFilters: updatedFilters.customFilters.push(getFilter(property, propInfo[index], value, config, dimensionOptions))
    });
  }, {
    dateRangeFilter: dateRange,
    customFilters: List()
  }));
};
const makeDimensionFilters = (config, keys, options = {}) => {
  return getDimensionFilters(config, keys, options).then(({
    dateRangeFilter,
    customFilters
  }) => {
    const filters = getFilters(config);
    const custom = getCustomFilters(config).concat(customFilters).toList();
    return filters.set('dateRange', dateRangeFilter).set('custom', custom);
  });
};
export const makeFilters = (config, keys, options = {}) => {
  const adjustedConfig = config.update('filters', fixCompareFilters(config, keys, Object.assign({
    shouldUseCompare: options.isComparisonPriorPeriod || options.datasetKey === 'compare'
  }, options)));
  return makeDimensionFilters(adjustedConfig, keys, options).then(withDimensionFilters => addMetricFilter(adjustedConfig, keys, withDimensionFilters));
};
export const makeFilterGroups = (config, keys, options = {}) => {
  const filterGroups = getFilterGroups(config);
  if (filterGroups.size === 0) {
    // Does not contain any filter groups
    return makeFilters(config, keys, options);
  }
  const adjustedConfig = config.update('filters', fixCompareFilters(config, keys, Object.assign({
    shouldUseCompare: options.isComparisonPriorPeriod || options.datasetKey === 'compare'
  }, options)));
  return Promise.all([getDimensionFilters(adjustedConfig, keys, options), getMetricFilter(adjustedConfig, keys)]).then(response => {
    const [dimensionFilters, metricFilters] = response;
    const updatedFilterGroups = applyFiltersToFilterGroups(filterGroups, dimensionFilters.customFilters.concat(metricFilters).toList());
    const currentFilters = getFilters(adjustedConfig);
    const updatedFilters = currentFilters.set('dateRange', dimensionFilters.dateRangeFilter).set('filterGroups', updatedFilterGroups);
    return updatedFilters;
  });
};